import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./PrintTestSheet.css";
import {
  FaPrint,
  FaFilePdf,
  FaImage,
  FaEdit,
  FaTimes,
  FaArrowLeft,
  FaCompress,
  FaExpand,
  FaAward,
  FaStamp
} from "react-icons/fa";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const PrintTestSheet = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const printRef = useRef(null);

  const students = location.state?.students || [];
  const formData = location.state?.formData || {};
  const className = location.state?.className || "N/A";

  // --- UPDATED DEFAULTS ---
  const [customSettings, setCustomSettings] = useState({
    watermark: "OFFICIAL RESULT",
    header: `${formData.testName || "Monthly Test"} Marks Sheet`,
    logo: "/images/l1.jpeg",      // UPDATED DEFAULT
    stamp: "/images/stamp.png",   // UPDATED DEFAULT
    showSignatures: true,
    showGrades: false,            // UPDATED DEFAULT (Hidden)
    showBubbles: true,
    fontSize: "medium", 
  });

  const [isCustomizing, setIsCustomizing] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [pageScale, setPageScale] = useState(1);

  const stats = {
    total: students.length,
    average: (
      students.reduce((sum, s) => sum + (s.obtainedMarks || 0), 0) /
        students.length || 0
    ).toFixed(1),
    passCount: students.filter((s) => (s.obtainedMarks / s.totalMarks) * 100 >= 50).length,
  };

  const getGrade = (percentage) => {
    if (percentage >= 90) return "A+";
    if (percentage >= 80) return "A";
    if (percentage >= 70) return "B";
    if (percentage >= 60) return "C";
    if (percentage >= 50) return "D";
    return "F";
  };

  const handleFileUpload = (e, target) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCustomSettings((prev) => ({ ...prev, [target]: event.target.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const downloadPDF = async () => {
    if (!printRef.current) return;
    const originalScale = pageScale;
    setPageScale(1); // Reset zoom for capture

    // Allow render cycle to update
    await new Promise((resolve) => setTimeout(resolve, 300));

    const element = printRef.current;
    const canvas = await html2canvas(element, {
      scale: 3, // High resolution
      useCORS: true,
      backgroundColor: "#ffffff",
      logging: false,
    });

    const imgData = canvas.toDataURL("image/png", 1.0);
    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
      compress: true,
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight, undefined, "FAST");
    pdf.save(`MarksSheet-${className}-${formData.testName}.pdf`);
    
    setPageScale(originalScale); // Restore zoom
  };

  return (
    <div className={`pts-page ${isFullScreen ? "pts-full-screen" : ""}`}>
      {/* Navbar Controls */}
      <div className="pts-header no-print">
        <button className="pts-back-btn" onClick={() => navigate(-1)}>
          <FaArrowLeft /> Back
        </button>
        <div className="pts-nav-center">
            <FaAward className="pts-main-icon" />
            <h2>Result Sheet Designer</h2>
        </div>
        <div className="pts-header-actions">
          <button className="pts-btn pts-btn-style" onClick={() => setIsCustomizing(!isCustomizing)}>
            <FaEdit /> {isCustomizing ? "Close Editor" : "Edit Styles"}
          </button>
          <button className="pts-btn pts-btn-pdf" onClick={downloadPDF}>
            <FaFilePdf /> Download
          </button>
          <button className="pts-btn pts-btn-print" onClick={() => window.print()}>
            <FaPrint /> Print
          </button>
          <button className="pts-btn pts-btn-expand" onClick={() => setIsFullScreen(!isFullScreen)}>
            {isFullScreen ? <FaCompress /> : <FaExpand />}
          </button>
        </div>
      </div>

      <div className="pts-main-container">
        {/* Editor Sidebar */}
        {isCustomizing && (
          <div className="pts-sidebar no-print">
            <h3>Customization</h3>
            
            <div className="pts-input-group">
              <label><FaImage /> School Logo</label>
              <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, "logo")} />
            </div>

            <div className="pts-input-group">
              <label><FaStamp /> Official Stamp</label>
              <input type="file" accept="image/*" onChange={(e) => handleFileUpload(e, "stamp")} />
            </div>

            <div className="pts-input-group">
              <label>Header Title</label>
              <input 
                type="text" 
                value={customSettings.header} 
                onChange={(e) => setCustomSettings({...customSettings, header: e.target.value})} 
              />
            </div>

            <div className="pts-input-group">
              <label>Watermark</label>
              <input 
                type="text" 
                value={customSettings.watermark} 
                onChange={(e) => setCustomSettings({...customSettings, watermark: e.target.value})} 
              />
            </div>

            <div className="pts-toggle-group">
              <label className="pts-checkbox">
                <input type="checkbox" checked={customSettings.showBubbles} onChange={(e) => setCustomSettings({...customSettings, showBubbles: e.target.checked})} />
                Decorative Background
              </label>
              <label className="pts-checkbox">
                <input type="checkbox" checked={customSettings.showGrades} onChange={(e) => setCustomSettings({...customSettings, showGrades: e.target.checked})} />
                Show Grades
              </label>
            </div>
          </div>
        )}

        {/* Paper Preview */}
        <div className="pts-preview-wrapper">
          <div 
            className={`pts-a4-paper ${customSettings.fontSize}`} 
            ref={printRef}
            style={{ transform: `scale(${pageScale})`, transformOrigin: "center top" }}
          >
            {/* Professional Background Elements (Soft Bubbles) */}
            {customSettings.showBubbles && (
              <div className="pts-bubbles-layer">
                <div className="pts-bubble b1"></div>
                <div className="pts-bubble b2"></div>
                <div className="pts-bubble b3"></div>
              </div>
            )}
            
            <div className="pts-watermark">{customSettings.watermark}</div>

            {/* Document Header */}
            <div className="pts-paper-header">
              <div className="pts-school-branding">
                {customSettings.logo ? (
                  <img src={customSettings.logo} alt="logo" className="pts-logo-img" />
                ) : (
                  <div className="pts-logo-placeholder"><FaAward /></div>
                )}
                <div className="pts-school-text">
                  <h1>DAR-E-ARQAM SCHOOL</h1>
                  <p>KOTLI LOHARAN EAST CAMPUS | SIALKOT</p>
                </div>
              </div>

              <div className="pts-document-badge">
                <h2>{customSettings.header}</h2>
              </div>

              <div className="pts-test-info-grid">
                <div className="pts-info-item"><strong>Subject:</strong> {formData.subject}</div>
                <div className="pts-info-item"><strong>Class:</strong> {className} ({formData.classSection})</div>
                <div className="pts-info-item"><strong>Date:</strong> {formData.date}</div>
                <div className="pts-info-item"><strong>Test ID:</strong> {formData.testId}</div>
              </div>
            </div>

            {/* Stats Summary */}
            <div className="pts-stats-bar">
                <div className="pts-stat-box"><span>Total Students</span><strong>{stats.total}</strong></div>
                <div className="pts-stat-box"><span>Avg. Marks</span><strong>{stats.average}</strong></div>
                <div className="pts-stat-box"><span>Passing</span><strong>{stats.passCount}</strong></div>
                <div className="pts-stat-box"><span>Total Marks</span><strong>{formData.totalMarks}</strong></div>
            </div>

            {/* Table Area */}
            <div className="pts-table-container">
              <table className="pts-main-table">
                <thead>
                  <tr>
                    <th style={{width: '40px'}}>#</th>
                    <th style={{width: '100px'}}>Roll No</th>
                    <th>Student Name</th>
                    <th>Father Name</th>
                    <th style={{width: '80px', textAlign:'center'}}>Obtained</th>
                    {customSettings.showGrades && <th style={{width: '60px', textAlign:'center'}}>Grade</th>}
                  </tr>
                </thead>
                <tbody>
                  {students.map((student, index) => {
                    const percentage = ((student.obtainedMarks / student.totalMarks) * 100);
                    // LOGIC CHANGE: If obtainedMarks is 0 or falsey, render "" (empty string)
                    const displayMarks = student.obtainedMarks ? student.obtainedMarks : "";
                    
                    return (
                      <tr key={student.id}>
                        <td className="text-center">{index + 1}</td>
                        <td className="pts-col-roll">{student.rollNumber || student.id}</td>
                        <td className="pts-col-name">{student.name}</td>
                        <td className="pts-col-father">{student.father_name || "-"}</td>
                        {/* Empty if 0 */}
                        <td className="pts-col-marks">{displayMarks}</td>
                        {customSettings.showGrades && (
                          <td className={`pts-col-grade grade-${getGrade(percentage)}`}>
                            {getGrade(percentage)}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer / Signatures / Stamp */}
            <div className="pts-paper-footer">
              <div className="pts-signature-section">
                <div className="pts-sig-block">
                  <div className="pts-sig-line"></div>
                  <p>Class Teacher</p>
                </div>

                {/* Professional Stamp Placement */}
                <div className="pts-stamp-placement">
                  {customSettings.stamp && (
                    <img src={customSettings.stamp} alt="stamp" className="pts-official-stamp" />
                  )}
                  {!customSettings.stamp && <div className="pts-stamp-circle">Stamp Here</div>}
                </div>

                <div className="pts-sig-block">
                  <div className="pts-sig-line"></div>
                  <p>Principal Signature</p>
                </div>
              </div>
              <div className="pts-footer-note">
                This is a computer-generated document. Errors and omissions excepted. | Printed on: {new Date().toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrintTestSheet;